import React from 'react';
import { PropTypes } from '../../../utils/propTypes';
import styles from './Empty.module.css';

/**
 * Empty — an empty-state layout, reimplemented in Athena's idiom (JSX + CSS
 * Module + 2.0 tokens). Pure layout/appearance; flat + mode-invariant. Compose
 * the action area (EmptyContent) with Button2 / InputGroup. Preview-only.
 *
 *   <Empty>
 *     <EmptyHeader>
 *       <EmptyMedia variant="icon"><Icon /></EmptyMedia>
 *       <EmptyTitle>…</EmptyTitle>
 *       <EmptyDescription>…</EmptyDescription>
 *     </EmptyHeader>
 *     <EmptyContent><Button>…</Button></EmptyContent>
 *   </Empty>
 */

const Empty = ({ className = '', children, ...props }) => (
  <div data-slot="empty" className={`${styles.empty} ${className}`} {...props}>
    {children}
  </div>
);

const EmptyHeader = ({ className = '', children, ...props }) => (
  <div data-slot="empty-header" className={`${styles.header} ${className}`} {...props}>
    {children}
  </div>
);

const EmptyMedia = ({ variant = 'default', className = '', children, ...props }) => (
  <div
    data-slot="empty-media"
    data-variant={variant}
    className={`${styles.media} ${variant === 'icon' ? styles.mediaIcon : ''} ${className}`}
    {...props}
  >
    {children}
  </div>
);

const EmptyTitle = ({ className = '', children, ...props }) => (
  <div data-slot="empty-title" className={`${styles.title} ${className}`} {...props}>
    {children}
  </div>
);

const EmptyDescription = ({ className = '', children, ...props }) => (
  <p data-slot="empty-description" className={`${styles.description} ${className}`} {...props}>
    {children}
  </p>
);

const EmptyContent = ({ className = '', children, ...props }) => (
  <div data-slot="empty-content" className={`${styles.content} ${className}`} {...props}>
    {children}
  </div>
);

Empty.propTypes = { className: PropTypes.string, children: PropTypes.node };
EmptyHeader.propTypes = { className: PropTypes.string, children: PropTypes.node };
EmptyMedia.propTypes = { variant: PropTypes.string, className: PropTypes.string, children: PropTypes.node };
EmptyTitle.propTypes = { className: PropTypes.string, children: PropTypes.node };
EmptyDescription.propTypes = { className: PropTypes.string, children: PropTypes.node };
EmptyContent.propTypes = { className: PropTypes.string, children: PropTypes.node };

export { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription, EmptyContent };
